import { MediaQueryService } from '@supply-mover-2-0/core';
import type { MediaAssetDto } from '@supply-mover-2-0/shared';
import { useCallback, useEffect, useRef, useState } from 'react';

import { contentTypeForFile } from './mediaFormat';

export type MediaUploadStatusModel =
	| 'uploading'
	| 'processing'
	| 'ready'
	| 'failed';

export interface MediaUploadEntryModel {
	key: string;
	name: string;
	/** 0 to 1 while the browser sends the file. */
	progress: number;
	status: MediaUploadStatusModel;
	asset: MediaAssetDto | null;
	/** English; the screen passes it through t(). */
	error: string | null;
}

export interface MediaUploadTargetModel {
	brandId: number | null;
	folderId?: number | null;
	/** Tags to put on each uploaded item, created when missing (e.g. "website"). */
	tagNames?: Array<string>;
}

/** How long to wait for the check and conversion before telling the person to look later. */
const PROCESSING_WAIT_MS = 120_000;
const POLL_INTERVAL_MS = 1_000;

/**
 * PUT the file to the signed address. XMLHttpRequest rather than fetch because fetch
 * reports no upload progress, and a 20 MB photo on a slow line needs a moving bar.
 */
function putToSignedUrl(
	url: string,
	file: File,
	contentType: string,
	onProgress: (fraction: number) => void,
): Promise<void> {
	return new Promise((resolve, reject) => {
		const xhr = new XMLHttpRequest();
		xhr.open('PUT', url);
		xhr.setRequestHeader('Content-Type', contentType);
		xhr.upload.onprogress = (event): void => {
			if (event.lengthComputable && event.total > 0) {
				onProgress(event.loaded / event.total);
			}
		};
		xhr.onload = (): void => {
			if (xhr.status >= 200 && xhr.status < 300) {
				resolve();
			} else {
				reject(new Error('The file could not be sent. Try again.'));
			}
		};
		xhr.onerror = (): void => {
			reject(
				new Error('The connection dropped while sending the file. Try again.'),
			);
		};
		xhr.send(file);
	});
}

function sleep(ms: number): Promise<void> {
	return new Promise((resolve) => {
		setTimeout(resolve, ms);
	});
}

/**
 * Wait for the server's check of a file to finish. Returns the item as it last read,
 * which is still processing when the wait ran out.
 */
async function waitForProcessing(
	assetId: number,
	isDone: (asset: MediaAssetDto) => boolean,
	isAlive: () => boolean,
): Promise<MediaAssetDto | null> {
	const deadline = Date.now() + PROCESSING_WAIT_MS;
	let last: MediaAssetDto | null = null;
	while (isAlive() && Date.now() < deadline) {
		const result = await MediaQueryService.get(assetId);
		if (result.success) {
			const current = result.data;
			last = current;
			if (isDone(current)) {
				return current;
			}
		}
		await sleep(POLL_INTERVAL_MS);
	}
	return last;
}

let nextKey = 0;

/**
 * PD-16339 — upload files into the media library and follow each one until the server
 * has checked it: ask for a signed address, send the file with progress, report it
 * arrived, then wait for "ready" or "failed" (with the reason in words).
 *
 * `replace` does the same for a new file of an existing item, keeping its address.
 */
export function useMediaUpload(
	options: {
		/** Called once per upload that reached ready or failed. */
		onSettled?: (asset: MediaAssetDto) => void;
	} = {},
): {
	entries: Array<MediaUploadEntryModel>;
	isBusy: boolean;
	upload: (
		files: ReadonlyArray<File>,
		target: MediaUploadTargetModel,
	) => Promise<Array<MediaAssetDto>>;
	replace: (assetId: number, file: File) => Promise<MediaAssetDto | null>;
	dismiss: (key: string) => void;
	clearFinished: () => void;
} {
	const [entries, setEntries] = useState<Array<MediaUploadEntryModel>>([]);
	const aliveRef = useRef(true);
	const onSettledRef = useRef(options.onSettled);
	onSettledRef.current = options.onSettled;

	useEffect(() => {
		aliveRef.current = true;
		return (): void => {
			aliveRef.current = false;
		};
	}, []);

	const patch = useCallback(
		(key: string, change: Partial<MediaUploadEntryModel>): void => {
			if (!aliveRef.current) {
				return;
			}
			setEntries((previous) =>
				previous.map((entry) =>
					entry.key === key ? { ...entry, ...change } : entry,
				),
			);
		},
		[],
	);

	const settle = useCallback(
		(key: string, asset: MediaAssetDto | null): void => {
			if (asset == null) {
				patch(key, {
					status: 'failed',
					error:
						'The file was not checked in time. Look for it in the library shortly.',
				});
				return;
			}
			if (asset.status === 'processing') {
				patch(key, {
					status: 'processing',
					asset,
					error:
						'Still being checked. It will appear in the library when it is ready.',
				});
				return;
			}
			patch(key, {
				status: asset.status === 'ready' ? 'ready' : 'failed',
				asset,
				error: asset.status === 'failed' ? asset.failureReason : null,
			});
			onSettledRef.current?.(asset);
		},
		[patch],
	);

	const uploadOne = useCallback(
		async (
			file: File,
			target: MediaUploadTargetModel,
		): Promise<MediaAssetDto | null> => {
			const key = `media-upload-${nextKey++}`;
			setEntries((previous) => [
				...previous,
				{
					key,
					name: file.name,
					progress: 0,
					status: 'uploading',
					asset: null,
					error: null,
				},
			]);

			const contentType = contentTypeForFile(file);
			const ticket = await MediaQueryService.createUpload({
				name: file.name,
				contentType,
				bytes: file.size,
				brandId: target.brandId,
				folderId: target.folderId ?? null,
				tagNames: target.tagNames,
			});
			if (!ticket.success) {
				patch(key, {
					status: 'failed',
					error: ticket.error ?? 'The upload could not start.',
				});
				return null;
			}

			try {
				await putToSignedUrl(
					ticket.data.uploadUrl,
					file,
					contentType,
					(progress) => {
						patch(key, { progress });
					},
				);
			} catch (error) {
				patch(key, {
					status: 'failed',
					error:
						error instanceof Error
							? error.message
							: 'The file could not be sent.',
				});
				return null;
			}

			patch(key, { progress: 1, status: 'processing' });
			const marked = await MediaQueryService.markUploaded(ticket.data.asset.id);
			if (!marked.success) {
				patch(key, {
					status: 'failed',
					error: marked.error ?? 'The upload could not be finished.',
				});
				return null;
			}

			const asset = await waitForProcessing(
				ticket.data.asset.id,
				(current) => current.status === 'ready' || current.status === 'failed',
				() => aliveRef.current,
			);
			settle(key, asset);
			return asset;
		},
		[patch, settle],
	);

	const upload = useCallback(
		async (
			files: ReadonlyArray<File>,
			target: MediaUploadTargetModel,
		): Promise<Array<MediaAssetDto>> => {
			// Two at a time: enough to keep the line busy without starving the rest of the page.
			const queue = [...files];
			const done: Array<MediaAssetDto> = [];
			const worker = async (): Promise<void> => {
				for (let file = queue.shift(); file != null; file = queue.shift()) {
					const asset = await uploadOne(file, target);
					if (asset != null) {
						done.push(asset);
					}
				}
			};
			await Promise.all([worker(), worker()]);
			return done;
		},
		[uploadOne],
	);

	const replace = useCallback(
		async (assetId: number, file: File): Promise<MediaAssetDto | null> => {
			const key = `media-upload-${nextKey++}`;
			setEntries((previous) => [
				...previous,
				{
					key,
					name: file.name,
					progress: 0,
					status: 'uploading',
					asset: null,
					error: null,
				},
			]);

			const contentType = contentTypeForFile(file);
			const ticket = await MediaQueryService.createReplaceUpload(assetId, {
				name: file.name,
				contentType,
				bytes: file.size,
			});
			if (!ticket.success) {
				patch(key, {
					status: 'failed',
					error: ticket.error ?? 'The replacement could not start.',
				});
				return null;
			}

			try {
				await putToSignedUrl(
					ticket.data.uploadUrl,
					file,
					contentType,
					(progress) => {
						patch(key, { progress });
					},
				);
			} catch (error) {
				patch(key, {
					status: 'failed',
					error:
						error instanceof Error
							? error.message
							: 'The file could not be sent.',
				});
				return null;
			}

			patch(key, { progress: 1, status: 'processing' });
			const marked = await MediaQueryService.markReplaceUploaded(
				assetId,
				ticket.data.fileId,
			);
			if (!marked.success) {
				patch(key, {
					status: 'failed',
					error: marked.error ?? 'The replacement could not be finished.',
				});
				return null;
			}

			const asset = await waitForProcessing(
				assetId,
				(current) => !current.replacing,
				() => aliveRef.current,
			);
			if (asset == null || asset.replacing) {
				settle(key, asset == null ? null : { ...asset, status: 'processing' });
				return asset;
			}
			// A refused replacement leaves the item as it was, with the reason on it.
			const refused =
				asset.version === ticket.data.asset.version &&
				asset.failureReason != null;
			patch(key, {
				status: refused ? 'failed' : 'ready',
				asset,
				error: refused ? asset.failureReason : null,
			});
			onSettledRef.current?.(asset);
			return asset;
		},
		[patch, settle],
	);

	const dismiss = useCallback((key: string): void => {
		setEntries((previous) => previous.filter((entry) => entry.key !== key));
	}, []);

	const clearFinished = useCallback((): void => {
		setEntries((previous) =>
			previous.filter(
				(entry) =>
					entry.status === 'uploading' || entry.status === 'processing',
			),
		);
	}, []);

	return {
		entries,
		isBusy: entries.some(
			(entry) =>
				entry.status === 'uploading' ||
				(entry.status === 'processing' && entry.error == null),
		),
		upload,
		replace,
		dismiss,
		clearFinished,
	};
}
